import { FormEvent, useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Alert, Button, CircularProgress } from "@mui/material";
import ReCAPTCHA from "react-google-recaptcha";
import {
  CalendarMonth,
  CheckCircle,
  Groups,
  PersonSearch,
  Schedule,
  School,
} from "@mui/icons-material";

import { validateParticipantCertificate } from "../../../API/apiParticipantCertificates";
import { CertificateValidationData } from "../../../types/ParticipantCertificate";
import { formatarDataDbParaBr } from "../../../utils/Masks";

import styles from "../Turmas/PresencaTurma.module.css";
import styles_button from "../../../styles/Button.module.css";
import styles_input from "../../../styles/Input.module.css";

const formatarHora = (hora?: string | null) => (hora ? hora.slice(0, 5) : "-");

const formatarPeriodo = (inicio?: string, fim?: string) => {
  if (!inicio && !fim) return "-";
  if (inicio === fim || !fim) return formatarDataDbParaBr(inicio || "");
  return `${formatarDataDbParaBr(inicio || "")} a ${formatarDataDbParaBr(fim)}`;
};

type ValidarCertificadoProps = {
  embedded?: boolean;
};

const ValidarCertificado = ({ embedded = false }: ValidarCertificadoProps) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const chaveInicial = searchParams.get("chave") || "";
  const recaptchaEnabled = process.env.REACT_APP_RECAPTCHA_ENABLED === "true";

  const [codigo, setCodigo] = useState(chaveInicial);
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState<string | null>(null);
  const [error, setError] = useState("");
  const [resultado, setResultado] = useState<CertificateValidationData | null>(null);

  const validacaoSucesso = useMemo(() => resultado?.validado === true, [resultado]);

  const validarCertificado = async (codigoValidacao: string) => {
    try {
      setLoading(true);
      setError("");
      const response = await validateParticipantCertificate(codigoValidacao);
      setResultado(response);

      if (!response.validado) {
        setError(response.message || "Certificado inválido.");
      }
    } catch (err) {
      setResultado(null);
      setError(err instanceof Error ? err.message : "Não foi possível validar o certificado.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (chaveInicial && !recaptchaEnabled) {
      validarCertificado(chaveInicial);
    }
  }, [chaveInicial, recaptchaEnabled]);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    const codigoNormalizado = codigo.trim();

    setError("");
    setResultado(null);

    if (!codigoNormalizado) {
      setError("Informe o código do certificado.");
      return;
    }

    if (recaptchaEnabled && !captchaToken) {
      setError("Confirme que você não é um robô.");
      return;
    }

    const nextParams = new URLSearchParams(searchParams);
    nextParams.set("chave", codigoNormalizado);
    setSearchParams(nextParams);

    await validarCertificado(codigoNormalizado);
  };

  const content = (
    <section className={embedded ? undefined : styles.card}>
        <div className={styles.header}>
          <CheckCircle />
          <div>
            <h1>Validação de Certificado</h1>
            <p>Informe o código do certificado para verificar se ele é válido.</p>
          </div>
        </div>

        <form className={styles.searchForm} onSubmit={handleSubmit}>
          <div className={styles.inputGroup}>
            <label htmlFor="codigo-certificado" className={styles_input.titleInput}>
              Código do certificado
            </label>
            <input
              id="codigo-certificado"
              className={`${styles_input.input} ${styles.cpfInput}`}
              value={codigo}
              onChange={(event) => setCodigo(event.target.value)}
              placeholder="Ex.: HEI-1660002"
            />
          </div>

          <Button
            type="submit"
            variant="contained"
            className={`${styles_button.buttonSquarePrimary} ${styles.searchButton}`}
            startIcon={loading ? <CircularProgress size={16} /> : <PersonSearch />}
            disabled={loading}
          >
            Validar
          </Button>
        </form>

        {recaptchaEnabled && (
          <div style={{ display: "flex", justifyContent: "center" }}>
            <ReCAPTCHA
              sitekey={process.env.REACT_APP_RECAPTCHA_SITE_KEY || ""}
              onChange={(token: string | null) => setCaptchaToken(token)}
              onExpired={() => setCaptchaToken(null)}
              onErrored={() => setCaptchaToken(null)}
            />
          </div>
        )}

        {error && <Alert severity="error">{error}</Alert>}

        {resultado && validacaoSucesso && (
          <>
            <Alert severity="success">{resultado.message || "Certificado válido."}</Alert>

            <div className={styles.courseInfo}>
              <div className={styles.courseTitle}>
                <School />
                <div>
                  <span>Certificado válido</span>
                  <strong>{resultado.acao || "-"}</strong>
                </div>
              </div>

              <div className={styles.infoGrid}>
                <div className={styles.infoItem}>
                  <Groups />
                  <div>
                    <span>Turma</span>
                    <strong>{resultado.turma || "-"}</strong>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <CalendarMonth />
                  <div>
                    <span>Período</span>
                    <strong>{formatarPeriodo(resultado.data_inicio, resultado.data_fim)}</strong>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <Schedule />
                  <div>
                    <span>Horário</span>
                    <strong>{formatarHora(resultado.hora_inicio)} às {formatarHora(resultado.hora_fim)}</strong>
                  </div>
                </div>
                <div className={styles.infoItem}>
                  <School />
                  <div>
                    <span>Carga horária</span>
                    <strong>{resultado.carga_horaria ?? "-"} horas</strong>
                  </div>
                </div>
              </div>
            </div>
          </>
        )}
    </section>
  );

  if (embedded) {
    return <div className={styles.card}>{content}</div>;
  }

  return <main className={styles.page}>{content}</main>;
};

export default ValidarCertificado;
